import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import WordCloud from "@/components/WordCloud";
import { AlertTriangleIcon } from 'lucide-react';

export default function Home() {
  const [answer, setAnswer] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [nonce, setNonce] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [locked, setLocked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(true);
  const [closedMessage, setClosedMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!nonce) {
      setMessage("Missing page token, try reloading the page.");
      return;
    }
    setLoading(true);
    try {
      // Validation
      if (answer.trim().length === 0) {
        setMessage("Atsakymas negali būti tuščias.");
        return;
      }
      if (answer.length > 200) {
        setMessage("Atsakymas negali būti ilgesnis nei 200 simbolių.");
        return;
      }
      // Only allow a single word (no whitespace)
      if (/\s/.test(answer.trim())) {
        setMessage("Įveskite vieną žodį (be tarpų).");
        return;
      }


      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer, honeypot, nonce }),
      });
      if (res.ok) {
        setSubmitted(true);
        setMessage("Ačiū — jūsų atsakymas priimtas peržiūrai.");
      } else {
        const data = await res.json().catch(() => ({ message: res.statusText }));
        const msg = data?.message ?? "Submission failed";
        setMessage(msg);
        // Lock the button when nonce-related or too-fast errors occur to avoid repeated invalid submissions
        if (res.status === 400 && /nonce|too fast|Submission too fast|Invalid or missing nonce/i.test(msg)) {
          setLocked(true);
        }
        if (res.status === 429) {
          // rate limited — optionally lock briefly
          setLocked(true);
        }
      }
    } catch (err: any) {
      setMessage(err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchNonce = async () => {
      try {
        const res = await fetch("/api/nonce");
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setNonce(data?.nonce ?? null);
      } catch (e) {
        // ignore
      }
    };
    fetchNonce();
    const fetchAccepting = async () => {
      try {
        const res = await fetch("/api/accepting");
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        setIsAccepting(Boolean(data.accepting));
        setClosedMessage(data.message ?? null);
      } catch (e) {
        // ignore
      }
    };
    fetchAccepting();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-8">
      <h1 className="text-4xl font-extrabold text-center text-white">KITM robotuko vardas</h1>

      <div className="w-full max-w-4xl">

        <Card className="mb-6 bg-white/20">
          {/* <CardHeader>
            <CardTitle>Tavo žodis = Tavo požiūris<br />Vienas žodis - Didelė mintis</CardTitle>
          </CardHeader> */}
          <CardContent>
            {!isAccepting ? (
              <div className="p-4 rounded-md bg-muted text-muted-foreground">{closedMessage ?? "Šiuo metu nepriimami atsakymai."}</div>
            ) : (
              <form onSubmit={handleSubmit} className="grid gap-4">
                <input
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: "absolute", left: "-9999px", top: "auto" }}
                />
                <div className="grid gap-1">
                  <Label>Sugalvok KITM robotukui vardą</Label>
                  <Input
                    value={answer}
                    onChange={(e) => setAnswer((e.target.value.match(/\S+/) || [''])[0])}
                  />
                </div>

                <div>
                  <Button type="submit" disabled={submitted || loading || !nonce || locked}>
                    {loading ? "Siunčiama..." : submitted ? "Atsakymas išsiųstas peržiūrai" : locked ? "Užblokuota" : "Siųsti"}
                  </Button>
                  <Alert variant="default" className="mt-4 border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50">
                    <AlertTriangleIcon />
                    <AlertTitle>Įspėjimas</AlertTitle>
                    <AlertDescription>Jūsų atsakymas bus peržiūrėtas prieš publikavimą.</AlertDescription>
                  </Alert>
                </div>
                {message ? (
                  <>
                    <div className="text-sm mt-2 text-destructive">{message}</div>
                    {
                      message.toLowerCase().includes("thank") || message.toLowerCase().includes("ačiū") ? (
                        <Button variant="outline" className="mt-2" onClick={() => {
                          // Reload the page
                          window.location.reload();
                        }}>Pateik naują atsakymą</Button>
                      ) : null
                    }
                  </>
                ) : null}
              </form>
            )}
          </CardContent>
        </Card>
        <div>
          <WordCloud maxWords={100} height={320} />
        </div>
      </div>
    </div>
  );
}
